import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// History.js announces `statechange` through jQuery's trigger(), and Bootstrap's affix plugin
// does the same, so both are bound in connect() and unbound in disconnect(). The category-tab
// click is a real DOM click and stays a data-action.
const JQUERY_NAMESPACE = ".untieCategories"

export default class extends Controller {
  static get targets() { return ["products", "categoriesList"]; }

  static get values() {
    return { path: String, skipPushState: Boolean, skipScrollTop: Boolean };
  }

  static get classes() { return ["active", "myFavoritesCategory"]; }

  // No markup declares an override for either class today, so these fall back to the literal
  // they replace via the has<Name>Class guard - see patterns.md #20. `this.activeClass` alone
  // would throw ("Missing attribute...") since Stimulus's Classes API does not default silently.
  get activeClassName() { return this.hasActiveClass ? this.activeClass : "active"; }
  get myFavoritesCategoryClassName() {
    return this.hasMyFavoritesCategoryClass ? this.myFavoritesCategoryClass : "my-favorites-category";
  }

  connect() {
    // A mount with no rendered categories list - categories and favoriting both disabled - does
    // nothing at all; favoriting server-renders only alongside this same list.
    if (!this.hasCategoriesListTarget) { return; }

    this.path = this.pathValue || window.location.pathname.replace(/\/$/, "")

    this.onStatechange = this.onStatechange.bind(this)
    if (!this.skipPushStateValue) {
      $(window).on(`statechange${JQUERY_NAMESPACE}`, this.onStatechange)
    }

    $(this.element).find(".categories-holder:not(.no-affix)").affix({
      offset: { top: () => $(this.productsTarget).offset().top }
    }).
      on(`affixed.bs.affix${JQUERY_NAMESPACE}`, this.onAffixed.bind(this)).
      on(`affix-top.bs.affix${JQUERY_NAMESPACE}`, this.onAffixTop.bind(this))

    window.ltFavoriteHelpers.initialize(document.querySelector(".products")?.dataset.redemptionToken)
  }

  disconnect() {
    $(window).off(JQUERY_NAMESPACE)

    const affixHolder = $(this.element).find(".categories-holder:not(.no-affix)")
    affixHolder.off(JQUERY_NAMESPACE)

    // Bootstrap 3's affix has no "destroy" command, so unbinding the scroll and click handlers it
    // put on its own $target, then dropping the data key, is the whole teardown.
    const affix = affixHolder.data("bs.affix")
    if (affix) {
      affix.$target.off(".bs.affix.data-api")
      affixHolder.removeData("bs.affix")
    }
  }

  onCategoryClick(event) {
    event.preventDefault()
    const target = event.currentTarget
    const favoritesTab = target.classList.contains(this.myFavoritesCategoryClassName)

    let categoryText = target.textContent
    // The My favorites tab is a special case of this.
    if (favoritesTab) { categoryText = "My Favorites"; }

    const category = encodeURIComponent(categoryText)
    let klass = target.getAttribute("class")
    klass = klass.replace(this.activeClassName, "")

    const data = { category, klass }

    if (this.skipPushStateValue) {
      this.setActiveTab(klass)
      this.updateProductsWith(data)
    } else {
      History.pushState(data, document.title, "?category=" + category)
    }

    if (!this.skipScrollTopValue) {
      $(window).animate({ scrollTop: $(".products-container").offset().top }, "50")
    }
  }

  toggleFavorite(event) {
    const { productId, redemptionToken, removeElement, real } = event.params
    window.ltFavoriteHelpers.toggleFavorite(productId, redemptionToken, removeElement, real)
  }

  onStatechange() {
    const state = History.getState()
    this.setActiveTab(state.data.klass)
    this.updateProductsWith(state.data)
  }

  onAffixed() {
    const $favoritePopover = $(".favorite-popover")
    if ($favoritePopover.length <= 0) { return; }

    const newTop = `calc(${$favoritePopover.css("top")} - ${$(this.element).find(".categories-holder:not(.no-affix)").height()}px)`
    $favoritePopover.css("top", newTop)
  }

  onAffixTop() {
    const $favoritePopover = $(".favorite-popover")
    if ($favoritePopover.length <= 0) { return; }

    const newTop = `calc(${$favoritePopover.css("top")} + ${$(this.element).find(".categories-holder:not(.no-affix)").height()}px)`
    $favoritePopover.css("top", newTop)
  }

  setActiveTab(klass) {
    this.element.querySelectorAll("li.category").forEach(el => el.classList.remove(this.activeClassName))
    this.element.querySelectorAll("li.category a").forEach(el => el.classList.remove(this.activeClassName))
    // Category classes come from parameterized tag names, so they can start with a digit
    // ("75-category"), which native selectors reject unless escaped.
    const classSelector = String(klass).split(/\s+/).filter(Boolean).map(name => `.${CSS.escape(name)}`).join("")
    if (!classSelector) { return; }

    this.element.querySelectorAll(`li.category a${classSelector}`).forEach(el => {
      el.classList.add(this.activeClassName)
      el.parentElement.classList.add(this.activeClassName)
    })
  }

  // The response is the product grid partial re-rendered for the new category. Its Add/Remove
  // buttons are data-actions targeting the ancestor `catalogs` controller, which Stimulus rebinds
  // as soon as this swap inserts them.
  //
  // S1-innerhtml: server-rendered HTML from our own origin - HasUntieable#render_untie renders
  // `partial: "unties/products"` (e.g. UntiesController#show) when this fetch's `partial=true`
  // param is set; Marketings::CollectionsController#show renders `partial: "products", layout:
  // false` for the marketings/collections mount. Neither app/views/unties/_products.html.haml nor
  // app/views/marketings/collections/_products.html.haml uses `raw`/`html_safe`/`!=`; product
  // name is auto-escaped and ProductDecorator#summary calls the real Rails `sanitize` via
  // ProductsHelper#sanitized_html.
  updateProductsWith(productInfo) {
    const category = productInfo.category
    const params = new URLSearchParams({ category: decodeURIComponent(category), partial: "true" })

    const options = RequestHelper.defaultRequestOptions()
    options.headers = Object.assign({}, options.headers, { "X-Requested-With": "XMLHttpRequest" })

    fetch(`${this.path}?${params}`, options).
      then(response => {
        if (!response.ok) { throw new Error(`Could not retrieve category ${category} (${response.status})`); }
        return response.text();
      }).
      then(html => {
        this.productsTarget.outerHTML = html;
        window.ltFavoriteHelpers.initialize(document.querySelector(".products")?.dataset.redemptionToken);
      }).
      catch(response => console.warn(`Could not retrieve category ${category}`, response))
  }
}
